import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { UtilsService } from '@app/core/services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadCrumb } from './bread-crumb';
import { CommonModule, NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MousePressDirective } from '@app/shared/directives/mouse-press.directive';
import { IVideoInputModel } from '../video-guide/models/video-input.model';
import { VideoGuideComponent } from '../video-guide/video-guide.component';
import { DashboardService } from '@app/views/pages/dashboard/services/dashboard.service';
import { Capacitor } from '@capacitor/core';
import { EBtnAttributeMatType } from '../new-components/new-button/types/attribute-mat.type';
import { EBtnHierarchyType } from '../new-components/new-button/types/hierarchy.type';
import { EBtnSizeType } from '../new-components/new-button/types/size.type';
import { NewButtonComponent } from '../new-components/new-button/new-button.component';
import { ResizeService } from '@app/core/services/resize.service';

@Component({
	selector: 'tc-bread-crumb',
	imports: [
		TranslateModule,
		CommonModule,
		MousePressDirective,
		MatButtonModule,
		RouterLink,
		VideoGuideComponent,
		NgClass,
		NewButtonComponent,
	],
	templateUrl: './bread-crumb.component.html',
	styleUrls: ['./bread-crumb.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class BreadCrumbComponent implements OnInit {

	// Input
	@Input() backUrl: string;
	@Input() video: IVideoInputModel;
	@Input() hasDownload: boolean = false;
	@Input() showDowloadAppMobileSize: boolean = true;
	@Input() showLinksBreadMobileSize: boolean = true;
	@Input() withBread: boolean = true;
	@Input() chatPageUrl: string;

	// Subject
	unsubscribeAll$ = new Subject<void>();

	// Subscription
	translateSubscription: Subscription;

	// models
	public breadcrumbs: BreadCrumb[];

	// string
	HOME = 'HOME';
	titlePage: string | null = null;
	formName: string;
	downloadAppUrl: string;

	// boolean
	isMobile: boolean;

	// type
	attributeVariantsType = EBtnAttributeMatType;
	hierarchyType = EBtnHierarchyType;
	sizeType = EBtnSizeType;

	constructor(
		private dashboardService: DashboardService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private utilsService: UtilsService,
		private translateService: TranslateService,
		private resizeService: ResizeService,
	) {
		this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
		this.checkSizeResize();
		this.handleSize();
	}

	//==================================
	// #region LIFE CYCLE HOOKS
	//==================================
	ngOnInit(): void {
		this.router.events
			.pipe(
				takeUntil(this.unsubscribeAll$),
				filter(event => event instanceof NavigationEnd),
				distinctUntilChanged()).subscribe(() => {
					this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
				});
	}

	ngOnDestroy(): void {
		this.unsubscribeAll$.next();
		this.unsubscribeAll$.complete();
	}


	//#endregion
	//==================================
	// #region PUBLIC
	//==================================
	get isNativePlatform(): boolean {
		return Capacitor.isNativePlatform();
	}

	getAndroinAppLink(): void {
		let applink = this.dashboardService.getSettings();
		this.downloadAppUrl = JSON.parse(applink)['androidLink'];
		const a = document.createElement('a');
		a.href = this.downloadAppUrl;
		a.click();
	}


	getTranslate(item: string): string {
		let translate: string;
		this.translateService.get('BREAD_CRUMP.' + item).subscribe(
			v => {
				translate = v;
			}
		);
		return translate;
	}

	back(): void {
		if (this.backUrl.includes('#')) {
			const url = this.backUrl.substring(0, this.backUrl.indexOf('#'));
			const fragment = this.backUrl.substring((this.backUrl.indexOf('#') + 1), (this.backUrl.length));
			this.utilsService.handleRouterNavigation(url, { fragment });
			return;
		}
		this.router.navigate([this.backUrl]);
	}

	saveLinkToStorage(): void {
		sessionStorage.setItem('backUrlChat', this.router.url);
	}

	guiedLink(): void {
		window.open('https://topchange.ladesk.com/', '_blank');
	}
	//#endregion
	//==================================
	// #region PRIVATE
	//==================================
	private checkSizeResize(): void {
		this.resizeService.onResize().subscribe(() => {
			this.handleSize();
		});
	}

	private handleSize(): void {
		let windowSize = window.innerWidth;
		if (windowSize >= 1120) {
			this.isMobile = false;
		} else {
			this.isMobile = true;
		}
	}

	private buildBreadCrumb(route: ActivatedRoute, url: string = '', breadcrumbs: BreadCrumb[] = []): BreadCrumb[] {

		const data = route.routeConfig?.data;
		const breadcrumbData = data?.breadcrumb;

		// مقدارهای پایه از روی data
		let label = breadcrumbData?.label ?? '';
		this.titlePage = breadcrumbData?.titlePage ?? '';
		this.backUrl = breadcrumbData?.backUrl ?? undefined;

		let path = route.routeConfig?.path ?? '';
		this.formName = data?.formName;

		// ====== نقطه‌ی داینامیک شدن label به صورت جنریک ======
		// اگر در data.breadcrumb تابع getLabel وجود داشت، صداش می‌زنیم
		const getLabelFn = breadcrumbData?.getLabel;
		if (typeof getLabelFn === 'function') {
			// هر روتی می‌تونه getLabel(route) تعریف کنه و هر دیتایی مثل summaryData رو از route بگیره
			label = getLabelFn(route);
		}
		// =====================================================

		const lastRoutePart = path.split('/').pop();
		const isDynamicRoute = lastRoutePart.startsWith(':');
		if (isDynamicRoute && !!route.snapshot) {
			const paramName = lastRoutePart.split(':')[1];
			path = path.replace(lastRoutePart, route.snapshot.params[paramName]);
		}
		const nextUrl = path ? `${url}/${path}` : url;

		const breadcrumb: BreadCrumb = { label };

		const dataUrl = breadcrumbData?.url;

		if (dataUrl !== null && dataUrl !== undefined && dataUrl !== '') {
			breadcrumb.url = dataUrl;
		}


		// Only adding route with non-empty label
		const Breadcrumbs = (breadcrumb && breadcrumb.label.length > 0) ? [...breadcrumbs, breadcrumb] : [...breadcrumbs];

		if (route.firstChild) {
			return this.buildBreadCrumb(route.firstChild, nextUrl, Breadcrumbs);
		}

		const newBreadCrumbs: BreadCrumb[] = [];
		for (const item of Breadcrumbs) {
			newBreadCrumbs.push({
				label: item.label.toUpperCase().replace(' ', '_'),
				...(item.url ? { url: item.url } : {})
			});
		}
		return newBreadCrumbs;
	}

	//#endregion
}